<template>
  <div
    class="project-bim-digital-compare"
    :class="{ 'project-bim-digital-compare--handbook-page': handbookPage }"
  >
    <div class="compare-controls">
    <div class="tw-mb-4">
      <div class="text-caption text-grey-7 q-mb-xs">Режим отчёта</div>
      <q-btn-toggle
        v-model="compareReportMode"
        spread
        no-caps
        dense
        unelevated
        toggle-color="primary"
        color="grey-3"
        text-color="grey-8"
        :options="modeToggleOptions"
        class="compare-mode-toggle"
      />
    </div>

    <q-banner v-if="compareReportMode === 'bimDigital' && blockWarning" dense rounded class="bg-orange-2 text-dark q-mb-md">
      {{ blockWarning }}
    </q-banner>

    <div class="tw-flex tw-flex-wrap tw-items-end tw-gap-4 tw-mb-4">
      <div style="min-width: 200px; flex: 1 1 200px;">
        <q-select
          v-model="selectedEntranceId"
          :options="entrances"
          option-value="project_entrance_id"
          option-label="entrance_name"
          emit-value
          map-options
          outlined
          dense
          label="Подъезд *"
          :loading="loadingEntrances"
        />
      </div>
      <div style="min-width: 200px; flex: 1 1 200px;">
        <q-select
          v-model="selectedLevelNum"
          :options="levelOptions"
          option-value="value"
          option-label="label"
          emit-value
          map-options
          outlined
          dense
          label="Этаж"
          clearable
          :disable="!selectedEntranceId"
        />
      </div>
      <q-btn
        unelevated
        color="primary"
        :label="compareReportMode === 'bimDigital' ? 'Сформировать отчёт' : 'Сформировать сравнение с сметой'"
        class="compare-submit-btn"
        :loading="loadingCompare"
        :disable="!selectedEntranceId"
        @click="runCompare"
      />
    </div>
    </div>

    <template v-if="compareReportMode === 'digitalSmeta'">
      <div
        class="smeta-mode-column"
        :class="{ 'smeta-mode-column--handbook': handbookPage }"
      >
      <div v-if="smetaVersionId != null" class="text-caption text-grey-7 q-mb-md">
        Версия сметы (последняя по проекту): <strong>{{ smetaVersionId }}</strong>
      </div>

      <div
        v-if="smetaHasRows"
        class="filter-joysticks q-mb-md"
      >
        <div class="text-caption text-grey-7 q-mb-sm">
          Фильтр по статусу сравнения объёма конструктива и по статусу каждого материала внутри конструктива; «Пусто» и «Только цифровой» без переключателей — всегда в таблице. Блок «Линии без сметы» — отдельным переключателем.
        </div>
        <div class="filter-joysticks__row">
          <q-toggle
            v-for="item in smetaStatusToggleDefs"
            :key="'smeta-' + item.status"
            :model-value="smetaFilterByStatus[item.status]"
            color="primary"
            dense
            :disable="loadingCompare"
            class="filter-joysticks__toggle"
            @update:model-value="(v) => onSmetaFilterStatusChange(item.status, v)"
          >
            <span class="text-body2">{{ item.label }}</span>
          </q-toggle>
        </div>
      </div>

      <div class="text-subtitle2 q-mb-sm">Конструктивы и материалы сметы</div>
      <div
        class="smeta-table-scroll"
        :class="{ 'smeta-table-scroll--handbook': handbookPage }"
      >
      <q-table
        flat
        bordered
        dense
        :wrap-cells="handbookPage"
        class="smeta-construct-table"
        :rows="filteredSmetaWorks"
        :columns="smetaWorkColumns"
        row-key="__key"
        :loading="loadingCompare"
        hide-pagination
        :pagination="smetaTablePagination"
        :no-data-label="smetaWorksEmptyLabel"
      >
        <template #header-cell-digital_qty_="props">
          <q-th :props="props" class="smeta-volume-th smeta-volume-th--digital">
            <span class="smeta-volume-th__l1">Объём</span>
            <span class="smeta-volume-th__l2">цифровой</span>
          </q-th>
        </template>
        <template #header-cell-smeta_qty_="props">
          <q-th :props="props" class="smeta-volume-th smeta-volume-th--smeta">
            <span class="smeta-volume-th__l1">Объём</span>
            <span class="smeta-volume-th__l2">смета</span>
          </q-th>
        </template>
        <template #body="props">
          <q-tr :props="props">
            <template
              v-for="col in smetaTableBodyColumns(props.cols)"
              :key="props.row.__key + '-' + col.name"
            >
              <q-td
                v-if="col.name === 'work_set_name_'"
                :key="col.name"
                :props="props"
                class="work-set-cell-td"
              >
                <span class="text-weight-medium">{{ workConstructLabel(props.row) }}</span>
                <span
                  v-if="props.row.unit_name_ || props.row.unit_name"
                  class="text-grey-7"
                > ({{ props.row.unit_name_ || props.row.unit_name }})</span>
                <q-badge
                  class="q-ml-xs"
                  dense
                  :color="statusColor(props.row.compare_status_ || props.row.compare_status)"
                  :label="statusLabel(props.row.compare_status_ || props.row.compare_status)"
                />
              </q-td>
              <!-- QTd не пробрасывает colspan — нативный td; ключ col.name для стабильности ряда -->
              <td
                v-else-if="col.name === 'materials_mat'"
                :key="col.name"
                colspan="3"
                class="q-td materials-merged-cell"
                :class="col.__tdClass != null ? col.__tdClass(props.row) : ''"
                :style="col.__tdStyle != null ? col.__tdStyle(props.row) : undefined"
              >
                <div
                  v-if="!filteredMaterialsForSmeta(props.row).length"
                  class="text-grey-6 text-caption materials-inner-empty"
                >
                  —
                </div>
                <div
                  v-else
                  class="materials-inner-wrap text-caption"
                >
                  <div
                    v-for="(m, midx) in filteredMaterialsForSmeta(props.row)"
                    :key="midx"
                    class="materials-inner-line"
                  >
                    <div class="materials-inner-cell materials-inner-cell--name">
                      <div class="materials-inner-name">
                        <span
                          class="materials-inner-name__text"
                          :title="materialLineTitle(m)"
                        >
                          <span class="text-weight-medium">{{ materialConstructLabel(m) }}</span>
                          <span
                            v-if="m.unit_name || m.unit_name_"
                            class="text-grey-7"
                          > ({{ m.unit_name || m.unit_name_ }})</span>
                        </span>
                        <q-badge
                          v-if="m.compare_status"
                          class="materials-inner-name__badge"
                          dense
                          :color="statusColor(m.compare_status)"
                          :label="statusLabel(m.compare_status)"
                        />
                      </div>
                    </div>
                    <div
                      class="materials-inner-cell materials-inner-cell--digital text-right"
                      :class="isNegativeQty(m.digital_qty) ? 'text-negative' : ''"
                    >
                      <template v-if="isNegativeQty(m.digital_qty)">
                        <q-icon name="warning" color="negative" size="xs" class="q-mr-xs" style="vertical-align: middle;" />
                        {{ formatMaterialQty(m.digital_qty) }}
                        <q-tooltip>Отрицательный объём — в смету не записывается</q-tooltip>
                      </template>
                      <template v-else>{{ formatMaterialQty(m.digital_qty) }}</template>
                    </div>
                    <div class="materials-inner-cell materials-inner-cell--smeta text-right">
                      {{ formatMaterialQty(m.smeta_qty) }}
                    </div>
                  </div>
                </div>
              </td>
              <q-td
                v-else-if="col.name === 'digital_qty_'"
                :key="col.name"
                :props="props"
                :class="isNegativeQty(col.value) ? 'text-negative' : ''"
              >
                <template v-if="isNegativeQty(col.value)">
                  <q-icon name="warning" color="negative" size="xs" class="q-mr-xs" style="vertical-align: middle;" />
                  {{ col.value }}
                  <q-tooltip>Отрицательный объём — в смету не записывается</q-tooltip>
                </template>
                <template v-else>{{ col.value }}</template>
              </q-td>
              <!-- Без содержимого слота QTd не рисует значение поля (в отличие от встроенной строки таблицы) -->
              <q-td
                v-else
                :key="col.name"
                :props="props"
              >
                {{ col.value }}
              </q-td>
            </template>
          </q-tr>
        </template>
      </q-table>
      </div>

      <div
        v-if="filteredLinesWithoutSmeta.length > 0"
        class="q-mt-lg"
      >
        <div class="text-subtitle2 q-mb-sm">Линии без сметы</div>
        <div class="text-caption text-grey-7 q-mb-sm">
          Типы этажа с ненулевым объёмом в цифре, не привязанные к формуле конструктива в охвате и к формуле материала (как в расчёте выше).
        </div>
        <div
          class="smeta-table-scroll"
          :class="{ 'smeta-table-scroll--handbook': handbookPage }"
        >
          <q-table
            flat
            bordered
            dense
            :wrap-cells="handbookPage"
            class="smeta-construct-table"
            :rows="filteredLinesWithoutSmeta"
            :columns="linesWithoutSmetaColumns"
            row-key="__key"
            :loading="loadingCompare"
            hide-pagination
            :pagination="linesWithoutSmetaPagination"
          />
        </div>
      </div>
      </div>
    </template>

    <template v-else>
      <div
        class="bim-mode-column"
        :class="{ 'bim-mode-column--handbook': handbookPage }"
      >
      <div
        v-if="rows.length > 0"
        class="filter-joysticks q-mb-md"
      >
        <div class="text-caption text-grey-7 q-mb-sm">
          Показать по статусу (сначала сформируйте отчёт, затем отключите ненужное)
        </div>
        <div class="filter-joysticks__row">
          <q-toggle
            v-for="item in bimStatusToggleDefs"
            :key="item.status"
            :model-value="filterByStatus[item.status]"
            color="primary"
            dense
            :disable="loadingCompare"
            class="filter-joysticks__toggle"
            @update:model-value="(v) => onFilterStatusChange(item.status, v)"
          >
            <span class="text-body2">{{ item.label }}</span>
          </q-toggle>
        </div>
      </div>

      <div
        class="bim-table-scroll"
        :class="{ 'bim-table-scroll--handbook': handbookPage }"
      >
      <q-table
        flat
        bordered
        dense
        :wrap-cells="handbookPage"
        :rows="filteredCompareRows"
        :columns="columns"
        row-key="__key"
        :loading="loadingCompare"
        hide-pagination
        :pagination="bimCompareTablePagination"
        :no-data-label="compareTableEmptyLabel"
      >
        <template #body-cell-compare_status="props">
          <q-td :props="props">
            <q-badge
              :color="statusColor(props.row.compare_status_ || props.row.compare_status)"
              :label="statusLabel(props.row.compare_status_ || props.row.compare_status)"
            />
          </q-td>
        </template>
      </q-table>
      </div>
      </div>
    </template>
  </div>
</template>

<script>
import { getEntrancesByProject, getBimDigitalParamCompare, getDigitalSmetaCompare } from '@/views/ProjectsEntry/services';
import { getEntranceApartments } from '@/views/ResidentialSettings/services';

function rowStatus(r) {
  return r.compare_status_ || r.compare_status || '';
}

export default {
  name: 'ProjectBimDigitalCompare',
  props: {
    projectId: { type: Number, required: true },
    residentId: { type: Number, default: null },
    projectTypeId: { type: Number, default: null },
    /** Полноэкранная колонка в отдельной вкладке BIM-смета: ширина и скролл только у таблицы */
    handbookPage: { type: Boolean, default: false },
  },
  data() {
    return {
      compareReportMode: 'bimDigital',
      modeToggleOptions: [
        { label: 'BIM — Цифровой', value: 'bimDigital' },
        { label: 'Цифровой — Смета', value: 'digitalSmeta' },
      ],
      /** Фильтры строк BIM — Цифровой (compare_status из bim_digital_param_compare__read). */
      bimStatusToggleDefs: [
        { status: 'mismatch', label: 'Расхождение' },
        { status: 'match', label: 'Совпадение' },
        { status: 'digital_only', label: 'Только цифровой' },
        { status: 'bim_only', label: 'Только BIM' },
      ],
      /** Фильтры режима Цифровой — Смета (compare_status в work_merge / mat_lines; no_smeta_line — блок сиротских линий). */
      smetaStatusToggleDefs: [
        { status: 'mismatch', label: 'Расхождение' },
        { status: 'match', label: 'Совпадение' },
        { status: 'smeta_only', label: 'Только смета' },
        { status: 'no_smeta_line', label: 'Линии без сметы' },
      ],
      filterByStatus: {
        mismatch: true,
        match: true,
        digital_only: true,
        bim_only: true,
      },
      smetaFilterByStatus: {
        mismatch: true,
        match: true,
        smeta_only: true,
        no_smeta_line: true,
      },
      entrances: [],
      loadingEntrances: false,
      selectedEntranceId: null,
      floorsPayload: [],
      selectedLevelNum: null,
      loadingCompare: false,
      rows: [],
      smetaWorks: [],
      linesWithoutSmeta: [],
      smetaVersionId: null,
      bimCompareTablePagination: {
        sortBy: 'floor_param_type_id',
        descending: false,
        page: 1,
        rowsPerPage: 0,
      },
      smetaTablePagination: {
        sortBy: 'work_set_name_',
        descending: false,
        page: 1,
        rowsPerPage: 0,
      },
      linesWithoutSmetaPagination: {
        sortBy: 'floor_param_type_id_',
        descending: false,
        page: 1,
        rowsPerPage: 0,
      },
      linesWithoutSmetaColumns: [
        {
          name: 'floor_param_type_id_',
          label: 'Id линии',
          field: (r) => r.floor_param_type_id_ ?? r.floor_param_type_id,
          align: 'right',
        },
        {
          name: 'line_formula_',
          label: 'Код линии (формула)',
          field: (r) => r.line_formula_ ?? r.material_name_ ?? r.material_name ?? '',
          align: 'left',
        },
        {
          name: 'work_set_name_',
          label: 'Название линии',
          field: (r) => r.work_set_name_ ?? r.work_set_name ?? '',
          align: 'left',
        },
        {
          name: 'digital_qty_',
          label: 'Кол-во (цифра, сумма по охвату)',
          field: (r) => r.digital_qty_ ?? r.digital_qty,
          align: 'right',
        },
      ],
      blockWarning: null,
      columns: [
        { name: 'floor_param_type_id', label: 'Id линии', field: 'floor_param_type_id', align: 'right' },
        { name: 'floor_param_type_name', label: 'Название линии', field: 'floor_param_type_name', align: 'left' },
        { name: 'compare_metric', label: 'Метрика', field: 'compare_metric', align: 'left' },
        { name: 'digital_total', label: 'Цифровой', field: 'digital_total', align: 'right' },
        { name: 'bim_total', label: 'BIM', field: 'bim_total', align: 'right' },
        { name: 'compare_status', label: 'Статус', field: 'compare_status', align: 'center' },
      ],
      smetaWorkColumns: [
        { name: 'work_set_name_', label: 'Конструктив (id / название)', field: 'work_set_name_', align: 'left' },
        { name: 'digital_qty_', label: 'Объём цифровой', field: 'digital_qty_', align: 'right' },
        { name: 'smeta_qty_', label: 'Объём смета', field: 'smeta_qty_', align: 'right' },
        { name: 'materials_mat', label: 'Материал (id / название)', field: 'materials_json_', align: 'left' },
        {
          name: 'materials_qty_digital',
          label: 'Кол-во, цифровой',
          field: 'materials_json_',
          align: 'right',
        },
        {
          name: 'materials_qty_smeta',
          label: 'Кол-во, смета',
          field: 'materials_json_',
          align: 'right',
        },
      ],
    };
  },
  computed: {
    levelOptions() {
      const list = this.floorsPayload || [];
      return list.map((f) => ({
        label: `Этаж ${f.floor}`,
        value: f.floor,
      }));
    },
    filteredCompareRows() {
      return this.rows.filter((r) => {
        const st = rowStatus(r);
        const on = this.filterByStatus[st];
        if (on === undefined) return true;
        return on === true;
      });
    },
    smetaHasRows() {
      return (this.smetaWorks?.length || 0) > 0 || (this.linesWithoutSmeta?.length || 0) > 0;
    },
    filteredSmetaWorks() {
      return this.filterSmetaRows(this.smetaWorks);
    },
    /** Строки «Линии без сметы» — отдельный переключатель no_smeta_line. */
    filteredLinesWithoutSmeta() {
      if (!this.smetaFilterByStatus.no_smeta_line) return [];
      return this.linesWithoutSmeta || [];
    },
    compareTableEmptyLabel() {
      if (!this.rows.length) {
        return 'Нет данных — выберите подъезд и нажмите «Сформировать отчёт»';
      }
      return 'Нет строк с включёнными статусами — включите переключатели';
    },
    smetaWorksEmptyLabel() {
      if (!this.smetaWorks.length) {
        return 'Нет строк — сформируйте сравнение или проверьте смету';
      }
      return 'Нет строк с выбранными статусами';
    },
  },
  watch: {
    selectedEntranceId() {
      this.selectedLevelNum = null;
      this.floorsPayload = [];
      this.rows = [];
      this.smetaWorks = [];
      this.linesWithoutSmeta = [];
      this.smetaVersionId = null;
      this.resetFiltersAllOn();
      this.resetSmetaFiltersAllOn();
      this.loadFloorsForEntrance();
    },
    compareReportMode(val, prev) {
      this.blockWarning = null;
      if (val === 'bimDigital') {
        this.smetaWorks = [];
        this.linesWithoutSmeta = [];
        this.smetaVersionId = null;
      } else {
        this.rows = [];
      }
      if (prev != null) {
        this.loadingCompare = false;
      }
    },
  },
  mounted() {
    this.loadEntrances();
  },
  methods: {
    smetaTableBodyColumns(cols) {
      return cols.filter(
        (c) =>
          c.name !== 'materials_qty_digital' && c.name !== 'materials_qty_smeta',
      );
    },
    normalizeMaterials(row) {
      let j = row?.materials_json_ ?? row?.materials_json;
      if (j == null || j === '') return [];
      if (typeof j === 'string') {
        try {
          j = JSON.parse(j);
        } catch (e) {
          return [];
        }
      }
      return Array.isArray(j) ? j : [];
    },
    formatMaterialQty(value) {
      if (value === null || value === undefined || value === '') return '—';
      const n = Number(value);
      if (Number.isNaN(n)) return String(value);
      return String(n);
    },
    /** Конструктив: work_set_id / название (как в справочнике). */
    workConstructLabel(row) {
      const id = row?.work_set_id_ ?? row?.work_set_id;
      const name = row?.work_set_name_ || row?.work_set_name || '';
      const idPart = id != null && id !== '' ? String(id) : null;
      if (idPart && name) return `${idPart} / ${name}`;
      if (idPart) return `${idPart} / —`;
      return name || '—';
    },
    /** Материал вложенной строки: material_id / название. */
    materialConstructLabel(m) {
      const id = m?.material_id ?? m?.material_id_;
      const name = m?.material_name || m?.material_name_ || '';
      const idPart = id != null && id !== '' ? String(id) : null;
      if (idPart && name) return `${idPart} / ${name}`;
      if (idPart) return `${idPart} / —`;
      return name || '—';
    },
    materialLineTitle(m) {
      const id = m?.material_id ?? m?.material_id_;
      const idPrefix = id != null && id !== '' ? `${id} / ` : '';
      const n = m?.material_name || m?.material_name_ || '';
      const u = m?.unit_name || m?.unit_name_;
      const core = u ? `${n} (${u})` : n;
      const t = idPrefix + core;
      const st = m?.compare_status ? ` — ${this.statusLabel(m.compare_status)}` : '';
      return t + st;
    },
    /**
     * Статусы конструктива и материала (не no_smeta_line): «Пусто» всегда проходит;
     * остальное — по включённым джойстикам.
     */
    smetaCompareStatusPasses(status) {
      const st = status || '';
      if (st === 'empty' || st === '') return true;
      const on = this.smetaFilterByStatus[st];
      if (on === undefined) return true;
      return on === true;
    },
    /** Материалы строки конструктива с учётом тех же переключателей статуса. */
    filteredMaterialsForSmeta(row) {
      return this.normalizeMaterials(row).filter((m) =>
        this.smetaCompareStatusPasses(m.compare_status),
      );
    },
    /**
     * Строка конструктива видна, если по фильтру проходит объём конструктива
     * или хотя бы один вложенный материал.
     */
    filterSmetaRows(list) {
      return (list || []).filter((r) => {
        const workPasses = this.smetaCompareStatusPasses(rowStatus(r));
        const anyMaterialPasses = this.normalizeMaterials(r).some((m) =>
          this.smetaCompareStatusPasses(m.compare_status),
        );
        return workPasses || anyMaterialPasses;
      });
    },
    resetFiltersAllOn() {
      this.filterByStatus = {
        mismatch: true,
        match: true,
        digital_only: true,
        bim_only: true,
      };
    },
    resetSmetaFiltersAllOn() {
      this.smetaFilterByStatus = {
        mismatch: true,
        match: true,
        smeta_only: true,
        no_smeta_line: true,
      };
    },
    onFilterStatusChange(statusKey, enabled) {
      const prevOn = Object.values(this.filterByStatus).filter(Boolean).length;
      const next = { ...this.filterByStatus, [statusKey]: enabled };
      const nextOn = Object.values(next).filter(Boolean).length;

      if (enabled && prevOn === 0) {
        this.resetFiltersAllOn();
        return;
      }
      if (!enabled && nextOn === 0) {
        this.resetFiltersAllOn();
        return;
      }
      this.filterByStatus = next;
    },
    onSmetaFilterStatusChange(statusKey, enabled) {
      const smetaToggleKeys = Object.keys(this.smetaFilterByStatus);
      const prevOn = smetaToggleKeys.filter((k) => this.smetaFilterByStatus[k]).length;
      const next = { ...this.smetaFilterByStatus, [statusKey]: enabled };
      const nextOn = smetaToggleKeys.filter((k) => next[k]).length;

      if (enabled && prevOn === 0) {
        this.resetSmetaFiltersAllOn();
        return;
      }
      if (!enabled && nextOn === 0) {
        this.resetSmetaFiltersAllOn();
        return;
      }
      this.smetaFilterByStatus = next;
    },
    isNegativeQty(value) {
      const n = Number(value);
      return !Number.isNaN(n) && n < 0;
    },
    statusColor(s) {
      const m = {
        match: 'positive',
        mismatch: 'negative',
        bim_only: 'info',
        digital_only: 'warning',
        smeta_only: 'info',
        empty: 'grey',
      };
      return m[s] || 'grey';
    },
    statusLabel(s) {
      const m = {
        match: 'Совпадение',
        mismatch: 'Расхождение',
        bim_only: 'Только BIM',
        digital_only: 'Только цифровой',
        smeta_only: 'Только смета',
        empty: 'Пусто',
      };
      return m[s] || s;
    },
    async loadEntrances() {
      this.loadingEntrances = true;
      try {
        const data = await getEntrancesByProject(this.projectId);
        this.entrances = data || [];
        if (this.entrances.length === 1) {
          this.selectedEntranceId = this.entrances[0].project_entrance_id;
        }
      } finally {
        this.loadingEntrances = false;
      }
    },
    async loadFloorsForEntrance() {
      if (!this.selectedEntranceId) {
        this.floorsPayload = [];
        return;
      }
      const params = { project_entrance_id: this.selectedEntranceId };
      if (this.residentId != null) params.resident_id = this.residentId;
      if (this.projectTypeId != null) params.project_type_id = this.projectTypeId;
      try {
        const res = await getEntranceApartments(params);
        this.floorsPayload = res?.data || [];
      } catch (e) {
        this.floorsPayload = [];
      }
    },
    runCompare() {
      if (this.compareReportMode === 'bimDigital') {
        this.loadCompare();
      } else {
        this.loadDigitalSmetaCompare();
      }
    },
    async loadCompare() {
      if (!this.selectedEntranceId) return;
      this.loadingCompare = true;
      this.blockWarning = null;
      try {
        const params = { project_entrance_id: this.selectedEntranceId };
        if (this.selectedLevelNum !== null && this.selectedLevelNum !== undefined) {
          params.level_num = this.selectedLevelNum;
        }
        const res = await getBimDigitalParamCompare(params);
        const data = res?.data || [];
        this.blockWarning = res?.block_warning || null;
        this.rows = data.map((row, i) => ({
          ...row,
          __key: `${row.floor_param_type_id}-${i}`,
        }));
        this.resetFiltersAllOn();
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.detail ||
          e?.message ||
          'Ошибка загрузки';
        this.$emit('error', msg);
      } finally {
        this.loadingCompare = false;
      }
    },
    async loadDigitalSmetaCompare() {
      if (!this.selectedEntranceId) return;
      this.loadingCompare = true;
      try {
        const params = { project_entrance_id: this.selectedEntranceId };
        if (this.selectedLevelNum !== null && this.selectedLevelNum !== undefined) {
          params.level_num = this.selectedLevelNum;
        }
        const res = await getDigitalSmetaCompare(params);
        const works = res?.works || [];
        const orphan = res?.lines_without_smeta || [];
        this.smetaVersionId = res?.smeta_version_id ?? null;
        this.smetaWorks = works.map((row, i) => ({
          ...row,
          __key: `ws-${row.work_set_id_ ?? row.work_set_id}-${i}`,
        }));
        this.linesWithoutSmeta = orphan.map((row, i) => ({
          ...row,
          __key: `orphan-${row.floor_param_type_id_ ?? row.floor_param_type_id}-${i}`,
        }));
        this.resetSmetaFiltersAllOn();
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.detail ||
          e?.message ||
          'Ошибка загрузки';
        this.$emit('error', msg);
      } finally {
        this.loadingCompare = false;
      }
    },
  },
};
</script>

<style scoped>
.project-bim-digital-compare--handbook-page {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overflow-x: hidden;
}

.compare-controls {
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
}

.smeta-mode-column--handbook,
.bim-mode-column--handbook {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overflow-x: hidden;
}

.smeta-table-scroll--handbook,
.bim-table-scroll--handbook {
  flex: 1 1 auto;
  min-height: 120px;
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
}

.smeta-construct-table {
  width: 100%;
}

/* Плотная сетка как в Excel: одинаковые отступы от края ячейки */
.smeta-construct-table :deep(thead th) {
  padding: 4px 8px !important;
}

.smeta-construct-table :deep(tbody td) {
  padding: 2px 8px !important;
}

/* Цифровой — тёплый кремовый фон; смета — голубой (6 колонок: конструктив | 2 объёма | 3 материала) */
.smeta-construct-table :deep(thead tr th:nth-child(2)),
.smeta-construct-table :deep(tbody tr td:nth-child(2)),
.smeta-construct-table :deep(thead tr th:nth-child(5)) {
  background-color: #f7f3e8 !important;
}

.smeta-construct-table :deep(thead tr th:nth-child(3)),
.smeta-construct-table :deep(tbody tr td:nth-child(3)),
.smeta-construct-table :deep(thead tr th:nth-child(6)) {
  background-color: #e8f2fa !important;
}

/* BIM — Цифровой: колонки «Цифровой» / «BIM» — те же фоны, что «Объём цифровой» / «Объём смета» */
.bim-table-scroll :deep(thead tr th:nth-child(4)),
.bim-table-scroll :deep(tbody tr td:nth-child(4)) {
  background-color: #f7f3e8 !important;
}

.bim-table-scroll :deep(thead tr th:nth-child(5)),
.bim-table-scroll :deep(tbody tr td:nth-child(5)) {
  background-color: #e8f2fa !important;
}

.smeta-volume-th {
  white-space: normal !important;
  line-height: 1.15;
  vertical-align: bottom;
}

.smeta-volume-th__l1 {
  display: block;
  font-weight: 600;
  font-size: 0.8rem;
}

.smeta-volume-th__l2 {
  display: block;
  font-size: 0.68rem;
  font-weight: 400;
  color: #616161;
}

.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(th.smeta-volume-th--digital),
.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(th.smeta-volume-th--smeta) {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.work-set-cell-td {
  vertical-align: top;
}

.project-bim-digital-compare--handbook-page :deep(.smeta-construct-table table),
.project-bim-digital-compare--handbook-page :deep(.bim-table-scroll--handbook table) {
  table-layout: fixed;
  width: 100%;
}

.project-bim-digital-compare--handbook-page :deep(.smeta-construct-table th),
.project-bim-digital-compare--handbook-page :deep(.smeta-construct-table td),
.project-bim-digital-compare--handbook-page :deep(.bim-table-scroll--handbook th),
.project-bim-digital-compare--handbook-page :deep(.bim-table-scroll--handbook td) {
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Цифра—смета: 6 колонок в шапке; тело — 4 ячейки (последняя colspan 3). Доли материалов 24:9:9 как у th 4–6 */
.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(thead tr th:nth-child(1)),
.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(tbody tr td:nth-child(1)) {
  width: 44%;
}

.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(thead tr th:nth-child(2)),
.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(tbody tr td:nth-child(2)),
.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(thead tr th:nth-child(3)),
.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(tbody tr td:nth-child(3)) {
  width: 7%;
  white-space: nowrap;
}

.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(thead tr th:nth-child(2)),
.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(thead tr th:nth-child(3)) {
  white-space: normal;
}

.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(thead tr th:nth-child(4)) {
  width: 24%;
}

.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(thead tr th:nth-child(5)),
.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(thead tr th:nth-child(6)) {
  width: 9%;
  white-space: nowrap;
}

.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(tbody tr td:nth-child(4)) {
  width: 42%;
}

.project-bim-digital-compare code {
  font-size: 0.85em;
}

.compare-mode-toggle {
  border-radius: 8px;
  overflow: hidden;
}

.compare-submit-btn {
  height: 40px;
  text-transform: none;
  border-radius: 4px;
}

.filter-joysticks {
  padding: 12px 14px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.filter-joysticks__row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  align-items: center;
}

.filter-joysticks__toggle {
  flex: 0 0 auto;
}

.project-bim-digital-compare--handbook-page .smeta-construct-table :deep(.materials-merged-cell) {
  vertical-align: top;
}

/* Три колонки материалов в одной ячейке: линия на всю ширину; min-width для фикс. раскладки outer table */
.smeta-construct-table :deep(.materials-merged-cell) {
  padding: 0 !important;
  vertical-align: top;
  min-width: 0;
}

.smeta-construct-table .materials-inner-empty {
  padding: 2px 8px;
}

/* Доли как у шапки th 4–6 (24% : 9% : 9%); minmax(0,…) — длинный материал не съедает колонки чисел */
.smeta-construct-table .materials-inner-wrap {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font-variant-numeric: tabular-nums;
}

.smeta-construct-table .materials-inner-line {
  display: grid;
  grid-template-columns: minmax(0, 24fr) minmax(0, 9fr) minmax(0, 9fr);
  align-items: start;
  box-sizing: border-box;
}

.smeta-construct-table .materials-inner-line + .materials-inner-line {
  border-top: 1px solid #a8a8a8;
}

.smeta-construct-table .materials-inner-cell {
  padding: 2px 8px;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.87);
  min-width: 0;
  box-sizing: border-box;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.smeta-construct-table .materials-inner-cell--digital {
  background-color: #f7f3e8 !important;
}

.smeta-construct-table .materials-inner-cell--smeta {
  background-color: #e8f2fa !important;
}

.smeta-construct-table .materials-inner-name {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.smeta-construct-table .materials-inner-name__text {
  min-width: 0;
  flex: 1 1 auto;
}

.smeta-construct-table .materials-inner-name__badge {
  flex-shrink: 0;
  line-height: 1;
}
</style>
